import React, { useState, useEffect } from "react";
import { Lock, Check, X } from "lucide-react";
import { useSendEmail } from "../hooks/useSendEmail";

interface NoAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentName?: string | undefined;
  ownerEmail?: string | undefined;
  ownerName?: string | undefined;
  requesterName?: string | undefined;
  requesterEmail?: string | undefined;
  autoSendRequest?: boolean;
}

const NoAccessModal: React.FC<NoAccessModalProps> = ({
  isOpen,
  onClose,
  documentName,
  ownerEmail,
  ownerName,
  requesterName,
  requesterEmail,
  autoSendRequest = false,
}) => {
  const { sendEmail } = useSendEmail();
  const [isRequesting, setIsRequesting] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendAccessRequest = async () => {
    if (!ownerEmail || !requesterEmail) return;

    setIsRequesting(true);
    setError(null);

    try {
      await sendEmail({
        to: ownerEmail,
        template: {
          subject: `Access Request: ${documentName || "Document"}`,
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
              <h2 style="color: #1d365d;">Document Access Request</h2>
              <p><strong>${requesterName || requesterEmail}</strong> has requested access to the document <strong>"${documentName || "Untitled Document"}"</strong>.</p>
              <p>You can grant access by opening the document in Data Revival and sharing it with them.</p>
              <p style="color: #666; font-size: 14px; margin-top: 20px;">Requester email: ${requesterEmail}</p>
            </div>
          `,
          text: `${requesterName || requesterEmail} has requested access to the document "${documentName || "Untitled Document"}". You can grant access by opening the document in Data Revival and sharing it with them. Requester email: ${requesterEmail}`,
        },
      });
      setRequestSent(true);
    } catch (err) {
      setError("Failed to send request. Please try again.");
    } finally {
      setIsRequesting(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const sendAccessRequest = async () => {
      if (isRequesting || !ownerEmail) return;
      setIsRequesting(true);
      setError(null);

      try {
        await sendEmail({
          to: ownerEmail,
          template: {
            subject: `Access Request for Document: ${documentName || "Untitled Document"}`,
            html: `
              <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                <h2 style="color: #1d365d;">Document Access Request</h2>
                <p><strong>${requesterName || requesterEmail}</strong> has requested access to the document <strong>"${documentName || "Untitled Document"}"</strong>.</p>
                <p>You can grant access by opening the document in Data Revival and sharing it with them.</p>
                <p style="color: #666; font-size: 14px; margin-top: 20px;">Requester email: ${requesterEmail}</p>
              </div>
            `,
            text: `${requesterName || requesterEmail} has requested access to the document "${documentName || "Untitled Document"}". You can grant access by opening the document in Data Revival and sharing it with them. Requester email: ${requesterEmail}`,
          },
        });
        if (!cancelled) {
          setRequestSent(true);
        }
      } catch (err) {
        if (!cancelled) {
          setError("Failed to send request. Please try again.");
        }
      } finally {
        if (!cancelled) {
          setIsRequesting(false);
        }
      }
    };

    if (isOpen && autoSendRequest && !requestSent && !isRequesting && ownerEmail) {
      sendAccessRequest();
    }

    return () => {
      cancelled = true;
    };
  }, [isOpen, autoSendRequest]);

  useEffect(() => {
    if (!isOpen) {
      setRequestSent(false);
      setError(null);
    }
  }, [isOpen]);

  const handleClose = () => {
    onClose();
  };

  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setVisible(true);
        });
      });
    } else {
      setVisible(false);
      const timer = setTimeout(() => setMounted(false), 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!mounted) return null;

  return (
    <div
      id="noaccessmodal"
      tabIndex={-1}
      className={`fixed inset-0 z-50 flex justify-center items-start w-full h-screen overflow-y-auto transition-opacity duration-200 ${
        visible
          ? "bg-black bg-opacity-50 opacity-100"
          : "opacity-0 pointer-events-none"
      }`}
      aria-hidden={!isOpen}
    >
      <div className="relative p-4 w-full max-w-md max-h-full mt-20">
        <div
          className={`relative bg-white rounded-lg shadow-sm p-4 md:p-6 transition-all duration-200 ${
            visible ? "opacity-100 scale-100" : "opacity-0 scale-95"
          }`}
        >
          <button
            type="button"
            className="absolute top-3 end-2.5 text-body bg-transparent hover:bg-neutral-tertiary hover:text-heading rounded-base text-sm w-9 h-9 inline-flex justify-center items-center"
            onClick={handleClose}
          >
            <X className="w-5 h-5" aria-hidden="true" />
            <span className="sr-only">Close modal</span>
          </button>

          {/* Modal body */}
          <div className="p-4 md:p-5 text-center">
            {requestSent ? (
              <>
                <Check className="mx-auto mb-4 text-green-500 w-12 h-12" />
                <h3 className="mb-2 text-lg font-semibold text-gray-900">
                  Request sent
                </h3>
                <p className="mb-6 text-gray-600">
                  Your access request has been sent to{" "}
                  {ownerName || "the document owner"}. They will be notified via
                  email.
                </p>
                <button
                  type="button"
                  className="text-white bg-[#1d365d] hover:bg-[#2d4a75] focus:ring-4 focus:ring-blue-300 shadow-xs font-medium rounded-xl text-sm px-6 py-2.5 focus:outline-none"
                  onClick={handleClose}
                >
                  Done
                </button>
              </>
            ) : (
              <>
                <Lock className="mx-auto mb-4 text-gray-400 w-12 h-12" />

                <h3 className="mb-2 text-lg font-semibold text-gray-900">
                  Access restricted
                </h3>

                <p className="mb-6 text-gray-600">
                  You don't have permission to access
                  {documentName ? (
                    <>
                      {" "}
                      <strong>"{documentName}"</strong>
                    </>
                  ) : (
                    " this document"
                  )}
                  .
                </p>

                {error && <p className="mb-4 text-red-600 text-sm">{error}</p>}

                <div className="flex gap-3 justify-center">
                  <button
                    type="button"
                    className="text-gray-700 bg-gray-100 hover:bg-gray-200 focus:ring-4 focus:ring-gray-300 shadow-xs font-medium rounded-xl text-sm px-6 py-2.5 focus:outline-none"
                    onClick={handleClose}
                  >
                    Cancel
                  </button>
                  {ownerEmail && (
                    <button
                      type="button"
                      className="text-white bg-[#1d365d] hover:bg-[#2d4a75] focus:ring-4 focus:ring-blue-300 shadow-xs font-medium rounded-xl text-sm px-6 py-2.5 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={sendAccessRequest}
                      disabled={isRequesting}
                    >
                      {isRequesting ? "Sending..." : "Request access"}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NoAccessModal;
